import { STATUS_ORDER } from './theme';

/** Conteos por estado → lista en el orden fijo; los estados desconocidos van al final. */
function orderStatuses(counts) {
    const known = STATUS_ORDER.filter((status) => counts[status]);
    const extra = Object.keys(counts)
        .filter((status) => counts[status] && !STATUS_ORDER.includes(status))
        .sort();
    return [...known, ...extra].map((status) => ({ status, total: counts[status] }));
}

/**
 * Pasa de filas estado×operador a un parque por operador, listo para pintar.
 * El orden es el del volumen: el operador más grande primero.
 */
export function groupByOperator(rows) {
    const byName = new Map();

    (rows || []).forEach((row) => {
        const name = row.mnoProviderName || '(sin operador)';
        let operator = byName.get(name);
        if (!operator) {
            operator = {
                name,
                total: 0,
                apio: 0,
                stripe: 0,
                ninguno: 0,
                counts: {},
            };
            byName.set(name, operator);
        }

        operator.total += row.total || 0;
        operator.apio += row.apio || 0;
        operator.stripe += row.stripe || 0;
        operator.ninguno += row.ninguno || 0;
        operator.counts[row.status] =
            (operator.counts[row.status] || 0) + (row.total || 0);
    });

    return [...byName.values()]
        .map((operator) => ({
            name: operator.name,
            total: operator.total,
            apio: operator.apio,
            stripe: operator.stripe,
            ninguno: operator.ninguno,
            activated: operator.counts.ACTIVATED || 0,
            statuses: orderStatuses(operator.counts),
        }))
        .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

/** Total del parque por estado, sumando todos los operadores. */
export function statusTotals(rows) {
    const counts = {};
    (rows || []).forEach((row) => {
        counts[row.status] = (counts[row.status] || 0) + (row.total || 0);
    });
    return orderStatuses(counts);
}

/**
 * Resumen de las suscripciones activas: cuántas hay, de qué operador son y
 * quién las cobra. Los operadores sin activas no entran en la lista.
 */
export function activeSummary(rows) {
    const active = (rows || []).filter((row) => row.status === 'ACTIVATED');
    const sum = (key) => active.reduce((acc, row) => acc + (row[key] || 0), 0);
    const byOperator = groupByOperator(active)
        .map((operator) => ({ name: operator.name, total: operator.total }))
        .filter((operator) => operator.total > 0);

    return {
        total: sum('total'),
        apio: sum('apio'),
        stripe: sum('stripe'),
        ninguno: sum('ninguno'),
        byOperator,
    };
}

/** Filas del detalle, mismo orden de operadores y de estados que el tablero. */
export function rowsByOperator(rows) {
    const operators = groupByOperator(rows);
    const rank = new Map(operators.map((operator, index) => [operator.name, index]));
    const statusRank = new Map(STATUS_ORDER.map((status, index) => [status, index]));

    return [...(rows || [])].sort((a, b) => {
        const operatorA = rank.get(a.mnoProviderName) ?? operators.length;
        const operatorB = rank.get(b.mnoProviderName) ?? operators.length;
        if (operatorA !== operatorB) return operatorA - operatorB;
        const statusA = statusRank.get(a.status) ?? STATUS_ORDER.length;
        const statusB = statusRank.get(b.status) ?? STATUS_ORDER.length;
        if (statusA !== statusB) return statusA - statusB;
        return b.total - a.total;
    });
}
